/**
 * type_aware renderer.
 *
 * A schema-aware generic renderer. Inspects each field's name and value
 * shape to pick an appropriate display, rather than dumping every field
 * into the same component. Produces visibly different layouts for
 * different tools without per-tool code, by following naming and value
 * conventions:
 *
 *   - Field named `to_email`, `cc`, `bcc`, ... → email chip
 *   - Field named `*_id` or value like `cus_…` / `ag_…` → ID badge
 *   - Field named `amount`, `*_cents`, `price_*` → currency
 *   - Field named `*_at`, `*_date`, `*_time` or ISO-8601 string → relative time
 *   - Long strings (>200 chars or multiline) → block text or markdown
 *   - URLs → clickable link
 *   - Arrays of short scalars → chip list
 *   - Nested objects → recursive sub-card
 *   - Fields starting with `_` → metadata (collapsed by default)
 *
 * Section grouping:
 *   - Fields matching `subject` → header
 *   - Fields matching `body` / `content` / `message` → body block
 *   - Fields prefixed `customer_*`, `agent_*` → grouped sub-section
 *   - Everything else → main grid
 *   - Fields starting with `_` or `previous_attributes` → collapsed details
 *
 * Includes a "Show raw JSON" escape hatch so the operator can fall back
 * to the unprocessed view when the heuristics get something wrong.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { ChevronDown, ChevronRight, Code as CodeIcon } from 'lucide-react'
import { snakeToTitleCase, EMPTY_PLACEHOLDER } from './shared'
import {
  Boolean as BooleanRenderer,
  ChipList,
  CodeBlock,
  CurrencyValue,
  DiffBlock,
  EmailChip,
  IdBadge,
  InlineString,
  Markdown,
  MultilineText,
  RegionChip,
  RelativeTime,
  UrlLink,
} from './typeAware/microRenderers'
import { normalizeGoMapString } from './typeAware/normalizeGoMapString'
import { useCardTranslatedText } from '../CardTranslation'
import { classifyField, type FieldClassification } from './typeAware/classifier'

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface TypeAwareRendererProps {
  data: unknown
}

export function TypeAwareRenderer({ data }: TypeAwareRendererProps) {
  const [showRaw, setShowRaw] = React.useState(false)

  // Normalise: anything not an object becomes { value: <data> }
  const root = normaliseRoot(data)

  if (root === null) {
    return <p style={styles.muted}>No data provided.</p>
  }

  if (showRaw) {
    return (
      <div style={styles.rawWrapper}>
        <button
          type="button"
          style={styles.rawToggle}
          onClick={() => setShowRaw(false)}
        >
          <ChevronDown style={styles.smallChevron} aria-hidden="true" />
          Show formatted view
        </button>
        <pre style={styles.rawPre}>{safeStringify(data)}</pre>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <ObjectRenderer obj={root} depth={0} />
      <button
        type="button"
        style={styles.escapeHatch}
        onClick={() => setShowRaw(true)}
        aria-expanded={false}
      >
        <CodeIcon style={styles.smallIcon} aria-hidden="true" />
        Show raw JSON
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Object → grouped sections
// ---------------------------------------------------------------------------

interface ObjectRendererProps {
  obj: Record<string, unknown>
  depth: number
}

interface FieldEntry {
  key: string
  value: unknown
  classification: FieldClassification
}

/**
 * Group order for sections within an object render.
 * Each group has a label, a predicate, and an order index.
 */
const GROUP_ORDER = [
  'header',
  'body',
  'customer',
  'agent',
  'main',
  'previous',
  'metadata',
] as const
type GroupKey = (typeof GROUP_ORDER)[number]

const GROUP_LABELS: Record<GroupKey, string | null> = {
  header: null, // no label — rendered above
  body: null, // no label — rendered prominently
  customer: 'Customer',
  agent: 'Agent',
  main: null, // no label — main grid
  previous: 'Previous Values',
  metadata: 'Metadata',
}

/**
 * Heuristic group assignment by field name.
 */
function groupForField(key: string, classification: FieldClassification): GroupKey {
  if (key.startsWith('_')) return 'metadata'
  if (key === 'previous_attributes') return 'previous'
  if (classification.kind === 'header') return 'header'
  if (classification.kind === 'body') return 'body'
  if (key.startsWith('customer_')) return 'customer'
  if (key.startsWith('agent_')) return 'agent'
  return 'main'
}

function ObjectRenderer({ obj, depth }: ObjectRendererProps) {
  // Empty object guard
  const keys = Object.keys(obj)
  if (keys.length === 0) {
    return <p style={styles.muted}>{EMPTY_PLACEHOLDER}</p>
  }

  // Classify every field once
  const entries: FieldEntry[] = keys.map((key) => ({
    key,
    value: obj[key],
    classification: classifyField(key, obj[key]),
  }))

  // Bucket into groups
  const groups = new Map<GroupKey, FieldEntry[]>()
  for (const entry of entries) {
    const group = groupForField(entry.key, entry.classification)
    const existing = groups.get(group) ?? []
    existing.push(entry)
    groups.set(group, existing)
  }

  // Strip empty/dropped fields from main if their classification said so
  return (
    <div style={depth === 0 ? styles.rootStack : styles.nestedStack}>
      {GROUP_ORDER.map((groupKey) => {
        const groupEntries = groups.get(groupKey)
        if (!groupEntries || groupEntries.length === 0) return null
        return (
          <FieldGroup
            key={groupKey}
            groupKey={groupKey}
            entries={groupEntries}
            depth={depth}
          />
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Group rendering
// ---------------------------------------------------------------------------

interface FieldGroupProps {
  groupKey: GroupKey
  entries: FieldEntry[]
  depth: number
}

function FieldGroup({ groupKey, entries, depth }: FieldGroupProps) {
  const label = GROUP_LABELS[groupKey]
  const collapsedByDefault = groupKey === 'metadata' || groupKey === 'previous'

  // Header group: render the value as a prominent title. Route through
  // FieldValueRenderer (rather than emitting a raw <h4> here) so headers share
  // the same per-classification dispatch as every other field — in particular
  // so foreign-language headers pick up the inline translate control.
  if (groupKey === 'header') {
    return (
      <div style={styles.headerGroup}>
        {entries.map(({ key, value, classification }) => (
          <FieldValueRenderer
            key={key}
            label={null}
            value={value}
            classification={classification}
            depth={depth}
          />
        ))}
      </div>
    )
  }

  // Body group: render the value in a prominent multiline block
  if (groupKey === 'body') {
    return (
      <div style={styles.bodyGroup}>
        {entries.map(({ key, value, classification }) => (
          <FieldValueRenderer
            key={key}
            label={null /* no label, body content stands alone */}
            value={value}
            classification={classification}
            depth={depth}
          />
        ))}
      </div>
    )
  }

  // Collapsible groups (metadata, previous)
  if (collapsedByDefault) {
    return (
      <CollapsibleGroup label={label ?? snakeToTitleCase(groupKey)} count={entries.length}>
        <FieldGrid entries={entries} depth={depth} />
      </CollapsibleGroup>
    )
  }

  // Labelled group (customer, agent)
  if (label) {
    return (
      <div style={styles.labelledGroup}>
        <h5 style={styles.groupLabel}>{label}</h5>
        <FieldGrid entries={entries} depth={depth} />
      </div>
    )
  }

  // Main group: bare grid
  return <FieldGrid entries={entries} depth={depth} />
}

interface FieldGridProps {
  entries: FieldEntry[]
  depth: number
}

/**
 * Whether a field kind renders as a full-height content block (markdown,
 * multiline text, nested objects, JSON strings, code, lists). These are
 * separated from compact inline fields so they can form their own auto-fit
 * grid and sit beside each other in wide containers (expanded modal) while
 * still stacking naturally in narrow containers (sidebar, right column).
 */
function isContentBlock(kind: FieldClassification['kind']): boolean {
  return (
    kind === 'markdown' ||
    kind === 'multiline' ||
    kind === 'body' ||
    kind === 'code' ||
    kind === 'nested' ||
    kind === 'json_string' ||
    kind === 'list'
  )
}

function FieldGrid({ entries, depth }: FieldGridProps) {
  // Split into compact inline rows and tall content blocks. The two groups
  // render in separate grids so content blocks can flow side-by-side in wide
  // containers without being pinned to full-width by `gridColumn: 1 / -1`.
  const inlineEntries = entries.filter((e) => !isContentBlock(e.classification.kind))
  const blockEntries = entries.filter((e) => isContentBlock(e.classification.kind))

  return (
    <div style={styles.fieldGridOuter}>
      {/* Compact label/value pairs: auto-fill so they share rows in wide panels */}
      {inlineEntries.length > 0 && (
        <dl style={styles.grid}>
          {inlineEntries.map(({ key, value, classification }) => (
            <FieldRow
              key={key}
              fieldKey={key}
              value={value}
              classification={classification}
              depth={depth}
            />
          ))}
        </dl>
      )}
      {/* Content blocks (text, nested objects): auto-fit so they flow into
          columns when the container is wide, and stack when it is narrow. */}
      {blockEntries.length > 0 && (
        <div style={styles.contentBlockGrid}>
          {blockEntries.map(({ key, value, classification }) => (
            <FieldRow
              key={key}
              fieldKey={key}
              value={value}
              classification={classification}
              depth={depth}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface FieldRowProps {
  fieldKey: string
  value: unknown
  classification: FieldClassification
  depth: number
}

function FieldRow({ fieldKey, value, classification, depth }: FieldRowProps) {
  const label = snakeToTitleCase(fieldKey)
  // Content blocks use a stacked (label-above-value) layout; compact scalars
  // use an inline (label-beside-value) layout. The block/inline split also
  // determines which sub-grid in FieldGrid the row lives in.
  const isBlock = isContentBlock(classification.kind)

  return (
    <div style={isBlock ? styles.rowStacked : styles.rowInline}>
      <dt style={styles.label}>{label}</dt>
      <dd style={styles.value}>
        <FieldValueRenderer
          label={null}
          value={value}
          classification={classification}
          depth={depth}
        />
      </dd>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Per-classification dispatch
// ---------------------------------------------------------------------------

interface FieldValueRendererProps {
  label: string | null
  value: unknown
  classification: FieldClassification
  depth: number
}

function FieldValueRenderer({ value, classification, depth }: FieldValueRendererProps) {
  // Empty / null / undefined
  if (value === null || value === undefined || value === '') {
    return <span style={styles.muted}>{EMPTY_PLACEHOLDER}</span>
  }

  switch (classification.kind) {
    case 'email':
      return <EmailChip email={String(value)} />

    case 'id_badge':
      return <IdBadge id={String(value)} />

    case 'currency':
      return (
        <CurrencyValue
          value={value as number}
          currency={classification.currency ?? 'USD'}
          isCents={classification.isCents}
        />
      )

    case 'datetime':
      return <RelativeTime value={value as string | number} />

    case 'url':
      return <UrlLink url={String(value)} />

    case 'boolean':
      return <BooleanRenderer value={Boolean(value)} />

    case 'markdown':
      return <Markdown text={String(value)} />

    case 'multiline':
    case 'body':
      return <MultilineText text={String(value)} />

    case 'code':
      return <CodeBlock value={value} />

    case 'header':
      return <TranslatableHeader text={String(value ?? '')} />


    case 'region':
      return <RegionChip code={String(value)} />

    case 'chips':
      return <ChipList items={value as unknown[]} />

    case 'list':
      return <ListRenderer items={value as unknown[]} depth={depth} />

    case 'nested':
      return (
        <div style={styles.nestedBlock}>
          <ObjectRenderer obj={value as Record<string, unknown>} depth={depth + 1} />
        </div>
      )

    case 'json_string': {
      // Field was classified as a JSON string (e.g. proposed_evidence_json).
      // Parse it and render using JsonStringRenderer — a flat key-value list
      // that always shows string values as text blocks regardless of length.
      // We deliberately avoid ObjectRenderer here because its prefix-based
      // grouping heuristics (customer_*, agent_*) produce misleading "CUSTOMER"
      // section boxes for evidence fields that happen to share that prefix.
      try {
        const parsed = JSON.parse(String(value)) as unknown
        if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return (
            <div style={styles.nestedBlock}>
              <JsonStringRenderer obj={parsed as Record<string, unknown>} />
            </div>
          )
        }
      } catch {
        // Corrupt JSON at render time — fall back to multiline text.
      }
      return <MultilineText text={String(value)} />
    }

    case 'inline':
    default: {
      // Task 4068a defensive safety net: a legacy item may carry a
      // Go-stringified `map[key:value ...]` blob as a plain string (the
      // exact prod-screenshot bug INVARIANT 8 forbids in the new model).
      // When the string cleanly matches that shape, re-render it as a keyed
      // object so the operator sees structured fields instead of raw `map[…]`.
      // Anything that doesn't cleanly match passes through unchanged.
      const goMap = normalizeGoMapString(value)
      if (goMap.matched) {
        return (
          <div style={styles.nestedBlock}>
            <ObjectRenderer obj={goMap.value} depth={depth + 1} />
          </div>
        )
      }
      // Short free-text fields (e.g. a one-line App Store review, a reviewer
      // nickname) land here. String values register with the card-level
      // translate control so foreign-language text flips with the rest of the
      // card; non-string values pass through untouched.
      if (typeof value === 'string') {
        return <TranslatableInline value={value} />
      }
      return <InlineString value={value} />
    }
  }
}

// ---------------------------------------------------------------------------
// Flat renderer for json_string fields
// ---------------------------------------------------------------------------

/**
 * Renders a parsed JSON object as a plain flat list of stacked label + value
 * rows. Used instead of ObjectRenderer for `json_string` fields so we avoid
 * the top-level grouping heuristics (customer_*, agent_* boxes) which produce
 * misleading section headers inside evidence payloads.
 *
 * All string values are shown as text blocks regardless of length — evidence
 * fields are meant to be read, not scanned, so a padded block is cleaner than
 * an inline value that wraps awkwardly.
 */
function JsonStringRenderer({ obj }: { obj: Record<string, unknown> }) {
  const entries = Object.entries(obj)
  if (entries.length === 0) {
    return <p style={styles.muted}>{EMPTY_PLACEHOLDER}</p>
  }
  return (
    <dl style={styles.jsonStringList}>
      {entries.map(([key, value]) => (
        <div key={key} style={styles.jsonStringRow}>
          <dt style={styles.label}>{snakeToTitleCase(key)}</dt>
          <dd style={styles.value}>
            {value === null || value === undefined ? (
              <span style={styles.muted}>{EMPTY_PLACEHOLDER}</span>
            ) : typeof value === 'string' ? (
              // Always use a text block for strings — don't apply the
              // multiline threshold that would render short strings inline.
              <MultilineText text={value} />
            ) : typeof value === 'object' && !Array.isArray(value) ? (
              <div style={styles.nestedBlock}>
                <ObjectRenderer obj={value as Record<string, unknown>} depth={1} />
              </div>
            ) : (
              <InlineString value={value} />
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}

// ---------------------------------------------------------------------------
// Card-translation-aware leaf renderers
// ---------------------------------------------------------------------------

/**
 * A `header`-classified value (subject, title, summary, ...) that participates
 * in the card-level translate control.
 */
function TranslatableHeader({ text }: { text: string }) {
  const shown = useCardTranslatedText(text)
  return <h4 style={styles.headerValue}>{shown}</h4>
}

/**
 * A short `inline` string value that participates in the card-level translate
 * control. `useCardTranslatedText` only registers it when it looks
 * non-English, so pure-ASCII fields are unaffected.
 */
function TranslatableInline({ value }: { value: string }) {
  const shown = useCardTranslatedText(value)
  return <InlineString value={shown} />
}

// ---------------------------------------------------------------------------
// List rendering (long arrays)
// ---------------------------------------------------------------------------

interface ListRendererProps {
  items: unknown[]
  depth: number
}

function ListRenderer({ items, depth }: ListRendererProps) {
  if (items.length === 0) {
    return <span style={styles.muted}>{EMPTY_PLACEHOLDER}</span>
  }

  return (
    <ul style={styles.list}>
      {items.map((item, idx) => {
        if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
          return (
            <li key={idx} style={styles.listItemBlock}>
              <ObjectRenderer obj={item as Record<string, unknown>} depth={depth + 1} />
            </li>
          )
        }
        return (
          <li key={idx} style={styles.listItem}>
            <InlineString value={item} />
          </li>
        )
      })}
    </ul>
  )
}

// ---------------------------------------------------------------------------
// Collapsible group wrapper
// ---------------------------------------------------------------------------

function CollapsibleGroup({
  label,
  count,
  children,
}: {
  label: string
  count: number
  children: React.ReactNode
}) {
  const [open, setOpen] = React.useState(false)
  return (
    <div style={styles.collapsibleGroup}>
      <button
        type="button"
        style={styles.collapsibleToggle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown style={styles.smallChevron} aria-hidden="true" />
        ) : (
          <ChevronRight style={styles.smallChevron} aria-hidden="true" />
        )}
        {label} <span style={styles.collapsibleCount}>({count})</span>
      </button>
      {open && <div style={styles.collapsibleBody}>{children}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normaliseRoot(data: unknown): Record<string, unknown> | null {
  if (data === null || data === undefined) return null
  if (typeof data !== 'object') return { value: data }
  if (Array.isArray(data)) return { items: data }
  return data as Record<string, unknown>
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

// Re-export DiffBlock for the per-section dispatch in the wider system
export { DiffBlock }

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  rootStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  nestedStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  // Responsive grid: inline field rows flow into auto-fill columns so that
  // short key-value pairs sit side by side in wide panels (maximised modal,
  // section expansion dialog) while still stacking in the narrow sidebar.
  // 280px is the minimum useful width for a label + short value.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '8px 20px',
    fontSize: '14px',
    margin: 0,
  },
  rowInline: {
    display: 'grid',
    gridTemplateColumns: 'minmax(120px, 160px) 1fr',
    gap: '12px',
    alignItems: 'baseline',
  },
  rowStacked: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    // No gridColumn override — rowStacked items now live in contentBlockGrid
    // which handles their own sizing, so they should naturally fill one column.
    minWidth: 0,
  },
  // Wrapper that stacks the two sub-grids (inline fields + content blocks).
  fieldGridOuter: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  // Content blocks (markdown, multiline text, nested objects, JSON strings)
  // live in their own auto-fit grid so they can sit side-by-side in wide
  // containers (full-width modal) while stacking naturally in narrow ones
  // (sidebar, half-width modal column). auto-fit collapses empty tracks so
  // a single block always stretches to fill the full available width.
  contentBlockGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '12px',
    alignItems: 'start',
  },
  // Flat list used by JsonStringRenderer. Simple vertical stack with no
  // responsive grid — these fields are always stacked since every value
  // renders as a text block.
  jsonStringList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    margin: 0,
    fontSize: '14px',
  },
  jsonStringRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    margin: 0,
    fontWeight: 500,
    color: 'var(--text-muted)',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  value: {
    margin: 0,
    color: 'var(--text-primary)',
    overflow: 'hidden',
  },
  muted: {
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },
  headerGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  headerValue: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 400,
    color: 'var(--text-primary)',
    lineHeight: 1.5,
  },
  bodyGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  labelledGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '10px 12px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
  },
  groupLabel: {
    margin: 0,
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--text-muted)',
  },
  collapsibleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  collapsibleToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    alignSelf: 'flex-start',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-muted)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  collapsibleCount: {
    color: 'var(--text-faint)',
  },
  collapsibleBody: {
    paddingLeft: '18px',
  },
  list: {
    margin: 0,
    paddingLeft: '0',
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  listItem: {
    padding: '4px 0',
    fontSize: '13px',
  },
  listItemBlock: {
    padding: '8px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
  },
  nestedBlock: {
    marginTop: '4px',
    padding: '10px 12px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
  },
  rawWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  rawToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    alignSelf: 'flex-start',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--accent-primary)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  rawPre: {
    maxHeight: '480px',
    overflow: 'auto',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-tertiary)',
    padding: '12px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
    margin: 0,
  },
  escapeHatch: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    alignSelf: 'flex-start',
    marginTop: '4px',
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-muted)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  smallChevron: {
    width: '12px',
    height: '12px',
  },
  smallIcon: {
    width: '12px',
    height: '12px',
  },
}

export default TypeAwareRenderer
